import { Fragment, ReactElement, useMemo } from 'react'
import convert from 'convert'
import { isNull } from 'lodash-es'
import numeral from 'numeral'
import {
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  tableCellClasses,
} from '@mui/material'
import { FormControl } from '@ui/forms'
import { UnitConvertedValueTypography } from '@ui/typography'
import { nmToNmm2 } from './AdditionalCheckInformation/utils'
import {
  computeFieldStiffnessDetails,
  vibrationCheckStatusStr,
  vibrationRequirementStr,
  vibrationScreedDataStr,
  vibrationScreedRequiredStr,
} from './VibrationCheckInformation/utils'

interface Props {
  check: VibrationCheck
}

const AdditionalVibrationCheckInformation = ({ check }: Props): ReactElement | null => {
  const fieldStiffnessDetails = useMemo(() => {
    if (check.stiffness_details == null) {
      return null
    }
    // Use computed field if available, otherwise compute from arrays
    if (check.stiffness_details.field_stiffness_details) {
      return check.stiffness_details.field_stiffness_details
    }
    return computeFieldStiffnessDetails(check.stiffness_details)
  }, [check.stiffness_details])

  const tableBody = useMemo(() => {
    return (
      <>
        {!isNull(check.requirement_category) && (
          <TableRow>
            <TableCell>Anforderungen</TableCell>
            <TableCell>{vibrationRequirementStr(check.requirement_category)}</TableCell>
          </TableRow>
        )}

        {!isNull(check.requirement_category) && !isNull(check.screed_required) && (
          <TableRow>
            <TableCell>Konstruktive Anforderungen</TableCell>
            <TableCell>{vibrationScreedRequiredStr(check.requirement_category)}</TableCell>
          </TableRow>
        )}

        {!isNull(check.screed_data) && check.screed_data && (
          <TableRow>
            <TableCell>Konstruktive Angaben</TableCell>
            <TableCell>{vibrationScreedDataStr(check.screed_data)}</TableCell>
          </TableRow>
        )}

        {!isNull(check.slab_weight_per_area) && (
          <TableRow>
            <TableCell>Aufbaugewicht</TableCell>
            <TableCell>
              {numeral(check.slab_weight_per_area).format('0,0.00a')} kg/m<sup>2</sup>
            </TableCell>
          </TableRow>
        )}

        {!isNull(check.step_size) && (
          <TableRow>
            <TableCell>Balkenabstand</TableCell>
            <TableCell>{numeral(check.step_size).format('0,0.000a')} m</TableCell>
          </TableRow>
        )}

        {!isNull(check.slab_width) && (
          <TableRow>
            <TableCell>Deckenbreite</TableCell>
            <TableCell>
              <FormControl>
                <UnitConvertedValueTypography
                  value={check.slab_width}
                  unitLabel="m"
                  conversionFactor={1}
                  numeralFormatter="0,0.00"
                />
              </FormControl>
            </TableCell>
          </TableRow>
        )}

        {!isNull(check.screed_e_modul) && (
          <TableRow>
            <TableCell>E-Modul Estrich</TableCell>
            <TableCell>
              {numeral(nmToNmm2(check.screed_e_modul)).format('0,0.')} N/mm<sup>2</sup>
            </TableCell>
          </TableRow>
        )}

        {!isNull(check.screed_height) && (
          <TableRow>
            <TableCell>Höhe Estrich</TableCell>
            <TableCell>
              {numeral(convert(check.screed_height, 'meter').to('millimeter')).format('0,0.00a')} mm
            </TableCell>
          </TableRow>
        )}

        {check.stiffness_details != null && (
          <>
            <TableRow>
              <TableCell colSpan={2}>
                <strong>Feldweise Steifigkeitswerte</strong>
              </TableCell>
            </TableRow>
            {fieldStiffnessDetails?.map((field, index) => (
              <>
                {index > 0 && (
                  <TableRow>
                    <TableCell colSpan={2}>
                      <Divider />
                    </TableCell>
                  </TableRow>
                )}
                <TableRow>
                  <TableCell>Feld</TableCell>
                  <TableCell>{index + 1}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>l</TableCell>
                  <TableCell>{numeral(field.span_length).format('0,0.00a')} m</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    EI<sub>l,Träger</sub>
                  </TableCell>
                  <TableCell>
                    {numeral(convert(field.member_stiffness_l, 'newton').to('kilonewton')).format(
                      '0,0.',
                    )}{' '}
                    kNm<sup>2</sup>/m
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    EI<sub>Estrich</sub>
                  </TableCell>
                  <TableCell>
                    {numeral(convert(field.screed_stiffness, 'newton').to('kilonewton')).format(
                      '0,0.',
                    )}{' '}
                    kNm<sup>2</sup>/m
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    EI<sub>l</sub>
                  </TableCell>
                  <TableCell>
                    {numeral(convert(field.bending_stiffness_l, 'newton').to('kilonewton')).format(
                      '0,0.',
                    )}{' '}
                    kNm<sup>2</sup>/m
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    EI<sub>b</sub>
                  </TableCell>
                  <TableCell>
                    {numeral(convert(field.bending_stiffness_b, 'newton').to('kilonewton')).format(
                      '0,0.',
                    )}{' '}
                    kNm<sup>2</sup>/m
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    b<sub>eff</sub>
                  </TableCell>
                  <TableCell>{numeral(field.effective_width).format('0,0.00a')} m</TableCell>
                </TableRow>
              </>
            ))}
          </>
        )}

        {check.frequency_details != null && (
          <>
            <TableRow>
              <TableCell>
                <strong>Frequenzkriterium</strong>
              </TableCell>
              <TableCell></TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                k<sub>f</sub>
              </TableCell>
              <TableCell>{numeral(check.frequency_details.k_f).format('0,0.00a')}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>l</TableCell>
              <TableCell>
                {numeral(check.frequency_details.span_length).format('0,0.00a')} m
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                EI<sub>l</sub>
              </TableCell>
              <TableCell>
                {numeral(
                  convert(check.frequency_details.stiffness_l, 'newton').to('kilonewton'),
                ).format('0,0')}{' '}
                kNm<sup>2</sup>/m
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                f<sub>e</sub>
              </TableCell>
              <TableCell>
                {numeral(check.frequency_details.natural_frequency).format('0,0.00a')} Hz
              </TableCell>
            </TableRow>
          </>
        )}

        {!isNull(check.frequency_min) && (
          <TableRow>
            <TableCell>
              f<sub>min</sub>
            </TableCell>
            <TableCell>{numeral(check.frequency_min).format('0,0.00a')} Hz</TableCell>
          </TableRow>
        )}

        {!isNull(check.frequency_limit) && (
          <TableRow>
            <TableCell>
              f<sub>grenz</sub>
            </TableCell>
            <TableCell>{numeral(check.frequency_limit).format('0,0.00a')} Hz</TableCell>
          </TableRow>
        )}

        {check.acceleration_details != null && (
          <>
            <TableRow>
              <TableCell>
                <strong>Beschleunigungskriterium</strong>
              </TableCell>
              <TableCell></TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                M<sup>*</sup>
              </TableCell>
              <TableCell>
                {numeral(check.acceleration_details.modal_mass).format('0,0.00a')} kg/m<sup>2</sup>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>α</TableCell>
              <TableCell>{numeral(check.acceleration_details.alpha).format('0,0.0000a')}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>D</TableCell>
              <TableCell>
                {numeral(check.acceleration_details.damping).format('0,0.000a')}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>l</TableCell>
              <TableCell>
                {numeral(check.acceleration_details.span_length).format('0,0.00a')} m
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                b<sub>eff</sub>
              </TableCell>
              <TableCell>
                {numeral(check.acceleration_details.effective_width).format('0,0.00a')} m
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>a</TableCell>
              <TableCell>
                {numeral(check.acceleration_details.acceleration).format('0,0.00a')} m/s<sup>2</sup>
              </TableCell>
            </TableRow>
          </>
        )}

        {!isNull(check.acceleration_limit) && (
          <TableRow>
            <TableCell>
              a<sub>grenz</sub>
            </TableCell>
            <TableCell>
              {numeral(check.acceleration_limit).format('0,0.00a')} m/s<sup>2</sup>
            </TableCell>
          </TableRow>
        )}

        {check.deflection_details != null && (
          <>
            <TableRow>
              <TableCell>
                <strong>Steifigkeitskriterium</strong>
              </TableCell>
              <TableCell></TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                EI<sub>l</sub>
              </TableCell>
              <TableCell>
                {numeral(
                  convert(check.deflection_details.bending_stiffness_l, 'newton').to('kilonewton'),
                ).format('0,0.')}{' '}
                kNm<sup>2</sup>/m
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>l</TableCell>
              <TableCell>
                {numeral(check.deflection_details.span_length).format('0,0.00a')} m
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                b<sub>eff</sub>
              </TableCell>
              <TableCell>
                {numeral(check.deflection_details.effective_width).format('0,0.00a')} m
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>w</TableCell>
              <TableCell>
                {numeral(
                  convert(check.deflection_details.deflection, 'meter').to('millimeter'),
                ).format('0,0.00a')}{' '}
                mm
              </TableCell>
            </TableRow>
          </>
        )}

        {!isNull(check.displacement_limit) && (
          <TableRow>
            <TableCell>
              w<sub>grenz</sub>
            </TableCell>
            <TableCell>
              {numeral(convert(check.displacement_limit, 'meter').to('millimeter')).format(
                '0,0.00a',
              )}{' '}
              mm
            </TableCell>
          </TableRow>
        )}
        <TableRow>
          <TableCell>
            <strong>Ergebnis</strong>
          </TableCell>
          <TableCell></TableCell>
        </TableRow>
        <TableRow>
          <TableCell>Ergebnis</TableCell>
          <TableCell>{vibrationCheckStatusStr(check.check_status)}</TableCell>
        </TableRow>
      </>
    )
  }, [check, fieldStiffnessDetails])

  if (isNull(tableBody)) return null

  return (
    <TableContainer>
      <Table
        stickyHeader
        size="small"
        sx={{
          [`& .${tableCellClasses.root}`]: {
            borderBottom: 'none',
            padding: 0,
            margin: 0,
          },
        }}
      >
        <TableBody>{tableBody}</TableBody>
      </Table>
    </TableContainer>
  )
}

export default AdditionalVibrationCheckInformation
